/**
 * Dialog per la gestione completa dei ruoli con permessi e menu
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import usersApiService, { Role, CreateRoleDto, UpdateRoleDto } from '../../services/usersApiService';

interface RoleManagementDialogProps {
  open: boolean;
  role?: Role | null;
  onClose: () => void;
  onSave: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`role-tabpanel-${index}`}
      aria-labelledby={`role-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const RoleManagementDialog: React.FC<RoleManagementDialogProps> = ({
  open,
  role,
  onClose,
  onSave,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [] as string[],
    menuPermissions: [] as string[],
  });

  const [availablePermissions, setAvailablePermissions] = useState<string[]>([]);
  const [availableMenuItems, setAvailableMenuItems] = useState<{ id: string; label: string; parent?: string }[]>([]);

  useEffect(() => {
    if (open) {
      loadAvailableOptions();
      if (role) {
        setFormData({
          name: role.name,
          description: role.description || '',
          permissions: role.permissions || [],
          menuPermissions: role.menuPermissions || [],
        });
      } else {
        setFormData({
          name: '',
          description: '',
          permissions: [],
          menuPermissions: [],
        });
      }
      setTabValue(0);
      setError(null);
    }
  }, [open, role]);

  const loadAvailableOptions = async () => {
    try {
      const [permissions, menuItems] = await Promise.all([
        usersApiService.getAvailablePermissions(),
        usersApiService.getAvailableMenuItems(),
      ]);
      setAvailablePermissions(permissions);
      setAvailableMenuItems(menuItems);
    } catch (err: any) {
      console.error('Error loading options:', err);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePermissionToggle = (permission: string) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const handleMenuToggle = (menuId: string) => {
    setFormData((prev) => ({
      ...prev,
      menuPermissions: prev.menuPermissions.includes(menuId)
        ? prev.menuPermissions.filter((m) => m !== menuId)
        : [...prev.menuPermissions, menuId],
    }));
  };

  const handleSelectAllPermissions = () => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.length === availablePermissions.length ? [] : [...availablePermissions],
    }));
  };

  const handleSelectAllMenus = () => {
    setFormData((prev) => ({
      ...prev,
      menuPermissions:
        prev.menuPermissions.length === availableMenuItems.length
          ? []
          : availableMenuItems.map((m) => m.id),
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!formData.name.trim()) {
        throw new Error('Il nome del ruolo è obbligatorio');
      }

      if (role) {
        // Update existing role
        const updateData: UpdateRoleDto = {
          name: formData.name,
          description: formData.description,
          permissions: formData.permissions,
          menuPermissions: formData.menuPermissions,
        };
        await usersApiService.updateRole(role.id, updateData);
      } else {
        // Create new role
        const createData: CreateRoleDto = {
          name: formData.name,
          description: formData.description,
          permissions: formData.permissions,
          menuPermissions: formData.menuPermissions,
        };
        await usersApiService.createRole(createData);
      }

      onSave();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore nel salvataggio del ruolo');
    } finally {
      setLoading(false);
    }
  };

  // Raggruppa i permessi per categoria
  const groupedPermissions = availablePermissions.reduce((acc, perm) => {
    const [category] = perm.split(':');
    if (!acc[category]) acc[category] = [];
    acc[category].push(perm);
    return acc;
  }, {} as Record<string, string[]>);

  // Raggruppa i menu item per parent
  const groupedMenuItems = availableMenuItems.reduce((acc, item) => {
    const key = item.parent || 'root';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {} as Record<string, typeof availableMenuItems>);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{role ? 'Modifica Ruolo' : 'Nuovo Ruolo'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Nome Ruolo"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            margin="normal"
            required
            disabled={loading}
          />
          <TextField
            fullWidth
            label="Descrizione"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            margin="normal"
            multiline
            rows={2}
            disabled={loading}
          />
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="role management tabs">
            <Tab label="Permessi Sistema" />
            <Tab label="Permessi Menu" />
            <Tab label="Riepilogo" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">Seleziona i permessi per questo ruolo:</Typography>
            <Button size="small" onClick={handleSelectAllPermissions}>
              {formData.permissions.length === availablePermissions.length ? 'Deseleziona Tutti' : 'Seleziona Tutti'}
            </Button>
          </Box>
          <FormGroup>
            {Object.entries(groupedPermissions).map(([category, perms]) => (
              <Box key={category} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', textTransform: 'capitalize' }}>
                  {category}
                </Typography>
                {perms.map((permission) => (
                  <FormControlLabel
                    key={permission}
                    control={
                      <Checkbox
                        checked={formData.permissions.includes(permission)}
                        onChange={() => handlePermissionToggle(permission)}
                        disabled={loading}
                      />
                    }
                    label={permission.replace(/:/g, ' → ')}
                  />
                ))}
              </Box>
            ))}
          </FormGroup>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">Seleziona le voci di menu visibili:</Typography>
            <Button size="small" onClick={handleSelectAllMenus}>
              {formData.menuPermissions.length === availableMenuItems.length ? 'Deseleziona Tutte' : 'Seleziona Tutte'}
            </Button>
          </Box>
          <FormGroup>
            {/* Menu principali */}
            {groupedMenuItems.root?.map((item) => (
              <FormControlLabel
                key={item.id}
                control={
                  <Checkbox
                    checked={formData.menuPermissions.includes(item.id)}
                    onChange={() => handleMenuToggle(item.id)}
                    disabled={loading}
                  />
                }
                label={item.label}
              />
            ))}

            {/* Sottomenu */}
            {Object.entries(groupedMenuItems).map(([parent, items]) => {
              if (parent === 'root') return null;
              return (
                <Box key={parent} sx={{ ml: 3, mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Sottomenu di {groupedMenuItems.root?.find((i) => i.id === parent)?.label || parent}
                  </Typography>
                  {items.map((item) => (
                    <FormControlLabel
                      key={item.id}
                      control={
                        <Checkbox
                          checked={formData.menuPermissions.includes(item.id)}
                          onChange={() => handleMenuToggle(item.id)}
                          disabled={loading}
                        />
                      }
                      label={item.label}
                    />
                  ))}
                </Box>
              );
            })}
          </FormGroup>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Riepilogo Ruolo
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Nome:</strong> {formData.name || '(non impostato)'}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Descrizione:</strong> {formData.description || '(non impostata)'}
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Permessi Sistema ({formData.permissions.length}):
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.permissions.length > 0 ? (
                formData.permissions.map((perm) => <Chip key={perm} label={perm} size="small" />)
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nessun permesso selezionato
                </Typography>
              )}
            </Box>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Voci Menu Visibili ({formData.menuPermissions.length}):
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.menuPermissions.length > 0 ? (
                formData.menuPermissions.map((menuId) => {
                  const menuItem = availableMenuItems.find((m) => m.id === menuId);
                  return <Chip key={menuId} label={menuItem?.label || menuId} size="small" color="primary" />;
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nessuna voce di menu selezionata
                </Typography>
              )}
            </Box>
          </Box>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annulla
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : role ? 'Aggiorna' : 'Crea'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleManagementDialog;
